import { forwardRef, MouseEvent } from 'react'

// Components
import Button from 'src/Components/shared/Button'
import { Text } from 'src/Components'
import { IconHeart, IconHeartEmpty } from 'src/Components/shared/Icon'
import StatusBadge from 'src/Components/homeSearch/StatusBadge'
import { Card } from './BaseCard'

import styles from './ListingCard.module.scss'
import { SearchListing } from 'src/types/listings'
import { MlsAbbrevAndNumber } from '../Compliance/MlsAbbrevAndNumber'

export interface ListingCardProps {
  listing: SearchListing
  isFavorite?: boolean
  allowFavorite?: boolean
  favoriteClick?: (e: MouseEvent<HTMLElement>, listing: SearchListing) => void
  focused?: boolean
  isSlim?: boolean
  className: string
}

const ListingCard = forwardRef<HTMLDivElement, ListingCardProps>(
  (
    {
      listing,
      isFavorite,
      allowFavorite = true,
      favoriteClick,
      focused = false,
      isSlim = false,
      className,
    }: ListingCardProps,
    ref
  ) => {
    const fullAddress = `${listing.address.number} ${listing.address.street} ${listing.address.city}, ${listing.address.stateOrProvince} ${listing.address.postalCode}`

    const mediumFont = isSlim ? 's' : 'm'
    const smallFont = isSlim ? 'xs' : 's'

    return (
      <Card
        ref={ref}
        focused={focused}
        slim={isSlim}
        className={`${className} ${styles.card}`}
      >
        <Card.Status>
          <StatusBadge status={listing.status} />
        </Card.Status>
        <Card.Image
          image={listing?.thumbnailImageFileNames[0]}
          alt={fullAddress}
          isSlim={isSlim}
        />
        <Card.Body>
          <div className={styles.bodyHeader}>
            <div>
              <Text
                element='h2'
                size='l'
                align='left'
                weight='bold'
                variant='neutralCool'
                variantCount='900'
              >
                {listing.priceFormatted}
              </Text>

              <div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {listing.bedroomsCount}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    beds
                  </Text>
                </div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {listing.bathroomsCount}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    baths
                  </Text>
                </div>
                <div className={styles.listingStat}>
                  <Text
                    element='span'
                    variant='neutralCool'
                    variantCount='700'
                    size={mediumFont}
                  >
                    {Math.round(listing.sqftTotal).toLocaleString()}{' '}
                  </Text>
                  <Text
                    element='span'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='800'
                    size={smallFont}
                  >
                    ft{'\u00B2'}
                  </Text>
                </div>
              </div>
            </div>

            {allowFavorite && (
              <Button
                className={styles.favoriteButton}
                data-qa='favoriteButton'
                iconStart={isFavorite ? IconHeart : IconHeartEmpty}
                aria-label='favorite toggle'
                aria-pressed={isFavorite}
                onClick={e => {
                  favoriteClick?.(e, listing)
                }}
              />
            )}
          </div>
          <div className={styles.bodyContent}>
            <div>
              <Text
                element='span'
                size={smallFont}
                variant='neutralCool'
                variantCount='600'
              >
                {listing.address.number} {listing.address.street}
              </Text>
            </div>
            <div>
              <Text
                element='span'
                size={smallFont}
                variant='neutralCool'
                variantCount='600'
              >
                {listing.address.city}, {listing.address.stateOrProvince}{' '}
              </Text>
              {listing.address.postalCode && (
                <Text
                  size={smallFont}
                  element='span'
                  variant='neutralCool'
                  variantCount='600'
                >
                  {listing.address.postalCode}
                </Text>
              )}
            </div>
            <MlsAbbrevAndNumber
              className={styles.mlsAbbrevAndNumber}
              listing={listing}
            />
          </div>
        </Card.Body>
      </Card>
    )
  }
)

export default ListingCard
